import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { psychologistStrings } from './psychologistStrings';

// Reusing styled components from Hatmada with psychological theme colors
const Header = styled.header`
  background-color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 50;
  direction: rtl;
`;

const HeaderContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const LogoTitle = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #2563eb;
  margin-bottom: 0.25rem;
`;

const LogoSubtitle = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const DesktopNav = styled.nav`
  display: none;
  gap: 2rem;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavButton = styled.button`
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.975rem;
  font-weight: 500;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
  background-color: ${props => props.active ? '#dbeafe' : 'transparent'};
  color: ${props => props.active ? '#2563eb' : '#374151'};
  
  &:hover {
    color: #2563eb;
    background-color: #dbeafe;
  }
`;

const MobileMenuButton = styled.button`
  display: block;
  padding: 0.5rem;
  border-radius: 0.375rem;
  color: #374151;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #2563eb;
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding: 1rem 0;
  border-top: 1px solid #e5e7eb;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileNavButton = styled.button`
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: right;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
  background-color: ${props => props.active ? '#dbeafe' : 'transparent'};
  color: ${props => props.active ? '#2563eb' : '#374151'};
  
  &:hover {
    color: #2563eb;
    background-color: #dbeafe;
  }
`;

const HeroSection = styled.section`
  background: linear-gradient(to bottom right, #dbeafe, #bfdbfe);
  padding: 5rem 0;
  direction: rtl;
`;

const SectionContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const HeroContent = styled.div`
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: #1e40af;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 1.5rem;
  color: #3730a3;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const TherapistInfo = styled.div`
  margin-bottom: 1rem;
`;

const TherapistName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const LicenseInfo = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const HeroMessage = styled.p`
  font-size: 1.125rem;
  color: #374151;
  margin-bottom: 2rem;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const PrimaryButton = styled.button`
  background-color: #2563eb;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #1d4ed8;
  }
`;

const SecondaryButton = styled.button`
  border: 2px solid #2563eb;
  color: #2563eb;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #2563eb;
    color: white;
  }
`;

const PageSection = styled.section`
  padding: 4rem 0;
  direction: rtl;
  background-color: ${props => props.alternate ? '#f8fafc' : 'white'};
`;

const PageContainer = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: #374151;
  text-align: center;
  margin-bottom: 3rem;
  line-height: 1.6;
`;

const PhilosophyText = styled.p`
  font-size: 1.125rem;
  color: #374151;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: justify;
`;

const TreatmentGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const TreatmentCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-right: 4px solid #2563eb;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 1rem;
`;

const TreatmentList = styled.ul`
  color: #374151;
  line-height: 1.6;
  
  li {
    margin-bottom: 0.75rem;
    position: relative;
    padding-right: 1.5rem;
    
    &:before {
      content: "•";
      position: absolute;
      right: 0;
      color: #2563eb;
      font-weight: bold;
    }
  }
`;

const EducationGrid = styled.div`
  display: grid;
  gap: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const EducationCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const InstitutionItem = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const InstitutionName = styled.h4`
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 0.25rem;
`;

const InstitutionDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
`;

const DegreeItem = styled.div`
  margin-bottom: 1rem;
`;

const DegreeLevel = styled.h4`
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 0.25rem;
`;

const DegreeField = styled.p`
  color: #374151;
  font-size: 0.875rem;
`;

const ResearchSection = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-top: 1.5rem;
`;

const ContactSection = styled.section`
  background-color: #2563eb;
  padding: 4rem 0;
  direction: rtl;
`;

const ContactContainer = styled.div`
  max-width: 42rem;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) {
    padding: 0 1.5rem;
  }
  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const ContactHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const ContactTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: white;
  margin-bottom: 1rem;
`;

const ContactSubtitle = styled.p`
  color: #bfdbfe;
`;

const ContactForm = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }
  
  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #2563eb;
  color: white;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #1d4ed8;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 0.5rem;
  padding: 1.5rem;
  color: white;
`;

const ContactInfoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const ContactInfoItem = styled.p`
  margin-bottom: 0.5rem;
  color: #bfdbfe;
`;

const EmergencyInfo = styled.div`
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  color: #fef2f2;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-left: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Footer = styled.footer`
  background: #1f2937;
  color: white;
  padding: 3rem 0 1rem;
  direction: rtl;
`;

const FooterContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;
`;

const FooterText = styled.p`
  color: #9ca3af;
  margin-bottom: 1rem;
`;

const PsychologistLandingPage = () => {
  const [currentSection, setCurrentSection] = useState('about');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Refs for sections
  const aboutRef = useRef(null);
  const treatmentRef = useRef(null);
  const educationRef = useRef(null);
  const contactRef = useRef(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.message) {
      alert(psychologistStrings.contact.form.fillAllFields);
      return;
    }

    setIsLoading(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone || 'לא צוין',
        message: formData.message,
        to_email: 'michael.psychology@gmail.com'
      };

      await emailjs.send(
        'service_w3pkjf6',
        'template_lwf20nq',
        templateParams,
        'Sgy1Do0paUmA8QyPR'
      );

      alert(`${psychologistStrings.contact.form.thankYou} ${formData.name}! ${psychologistStrings.contact.form.successMessage}`);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      alert(psychologistStrings.contact.form.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToSection = (sectionRef, sectionName) => {
    sectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
    setCurrentSection(sectionName);
    setIsMenuOpen(false);
  };

  const navItems = [
    { id: 'about', label: psychologistStrings.navigation.about, ref: aboutRef },
    { id: 'treatment', label: psychologistStrings.navigation.treatmentAreas, ref: treatmentRef },
    { id: 'education', label: psychologistStrings.navigation.education, ref: educationRef },
    { id: 'contact', label: psychologistStrings.navigation.contact, ref: contactRef }
  ];

  return (
    <div>
      {/* Header */}
      <Header>
        <HeaderContainer>
          <HeaderContent>
            <Logo>
              <LogoTitle>{psychologistStrings.global.instituteName}</LogoTitle>
              <LogoSubtitle>{psychologistStrings.global.subtitle}</LogoSubtitle>
            </Logo>

            <DesktopNav>
              {navItems.map((item) => (
                <NavButton
                  key={item.id}
                  active={currentSection === item.id}
                  onClick={() => scrollToSection(item.ref, item.id)}
                >
                  {item.label}
                </NavButton>
              ))}
            </DesktopNav>

            <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <span style={{ fontSize: '1.5rem' }}>✕</span> : <span style={{ fontSize: '1.5rem' }}>☰</span>}
            </MobileMenuButton>
          </HeaderContent>

          <MobileMenu isOpen={isMenuOpen}>
            <MobileNav>
              {navItems.map((item) => (
                <MobileNavButton
                  key={item.id}
                  active={currentSection === item.id}
                  onClick={() => scrollToSection(item.ref, item.id)}
                >
                  {item.label}
                </MobileNavButton>
              ))}
            </MobileNav>
          </MobileMenu>
        </HeaderContainer>
      </Header>

      {/* Hero Section */}
      <HeroSection>
        <SectionContainer>
          <HeroContent>
            <HeroTitle>{psychologistStrings.hero.title}</HeroTitle>
            <HeroSubtitle>{psychologistStrings.hero.subtitle}</HeroSubtitle>
            <TherapistInfo>
              <TherapistName>{psychologistStrings.hero.therapistInfo}</TherapistName>
              <LicenseInfo>{psychologistStrings.hero.license}</LicenseInfo>
            </TherapistInfo>
            <HeroMessage>{psychologistStrings.hero.mainMessage}</HeroMessage>
            <ButtonGroup>
              <PrimaryButton onClick={() => scrollToSection(contactRef, 'contact')}>
                {psychologistStrings.hero.primaryButton}
              </PrimaryButton>
              <SecondaryButton onClick={() => scrollToSection(aboutRef, 'about')}>
                {psychologistStrings.hero.secondaryButton}
              </SecondaryButton>
            </ButtonGroup>
          </HeroContent>
        </SectionContainer>
      </HeroSection>

      {/* About Section */}
      <PageSection ref={aboutRef}>
        <PageContainer>
          <SectionTitle>{psychologistStrings.about.title}</SectionTitle>
          <PhilosophyText>{psychologistStrings.about.philosophy1}</PhilosophyText>
          <PhilosophyText>{psychologistStrings.about.philosophy2}</PhilosophyText>
          <PhilosophyText>{psychologistStrings.about.philosophy3}</PhilosophyText>
        </PageContainer>
      </PageSection>

      {/* Treatment Areas Section */}
      <PageSection ref={treatmentRef} alternate>
        <PageContainer>
          <SectionTitle>{psychologistStrings.treatmentAreas.title}</SectionTitle>
          <SectionSubtitle>{psychologistStrings.treatmentAreas.subtitle}</SectionSubtitle>
          
          <TreatmentGrid>
            <TreatmentCard>
              <CardTitle>{psychologistStrings.treatmentAreas.childrenAndAdolescents.title}</CardTitle>
              <TreatmentList>
                {psychologistStrings.treatmentAreas.childrenAndAdolescents.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </TreatmentList>
            </TreatmentCard>

            <TreatmentCard>
              <CardTitle>{psychologistStrings.treatmentAreas.adults.title}</CardTitle>
              <TreatmentList>
                {psychologistStrings.treatmentAreas.adults.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </TreatmentList>
            </TreatmentCard>

            <TreatmentCard>
              <CardTitle>{psychologistStrings.treatmentAreas.clinicalIssues.title}</CardTitle>
              <TreatmentList>
                {psychologistStrings.treatmentAreas.clinicalIssues.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </TreatmentList>
            </TreatmentCard>
          </TreatmentGrid>
        </PageContainer>
      </PageSection>

      {/* Education Section */}
      <PageSection ref={educationRef}>
        <PageContainer>
          <SectionTitle>{psychologistStrings.education.title}</SectionTitle>
          
          <EducationGrid>
            <EducationCard>
              <CardTitle>{psychologistStrings.education.experience.title}</CardTitle>
              {psychologistStrings.education.experience.institutions.map((institution, index) => (
                <InstitutionItem key={index}>
                  <InstitutionName>{institution.name}</InstitutionName>
                  {institution.description && (
                    <InstitutionDescription>{institution.description}</InstitutionDescription>
                  )}
                </InstitutionItem>
              ))}
              
              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <InstitutionName>ניסיון מקצועי נוסף:</InstitutionName>
                {psychologistStrings.education.experience.additional.map((item, index) => (
                  <InstitutionDescription key={index} style={{ marginBottom: '0.5rem' }}>
                    • {item}
                  </InstitutionDescription>
                ))}
              </div>
            </EducationCard>

            <EducationCard>
              <CardTitle>{psychologistStrings.education.academicBackground.title}</CardTitle>
              {psychologistStrings.education.academicBackground.degrees.map((degree, index) => (
                <DegreeItem key={index}>
                  <DegreeLevel>{degree.level}</DegreeLevel>
                  <DegreeField>{degree.field}</DegreeField>
                </DegreeItem>
              ))}
              
              <ResearchSection>
                <DegreeLevel>{psychologistStrings.education.academicBackground.research.title}</DegreeLevel>
                <DegreeField style={{ marginBottom: '0.5rem' }}>
                  {psychologistStrings.education.academicBackground.research.description}
                </DegreeField>
                <DegreeField style={{ fontStyle: 'italic' }}>
                  {psychologistStrings.education.academicBackground.research.supervisor}
                </DegreeField>
              </ResearchSection>
            </EducationCard>
          </EducationGrid>
        </PageContainer>
      </PageSection>

      {/* Contact Section */}
      <ContactSection ref={contactRef}>
        <ContactContainer>
          <ContactHeader>
            <ContactTitle>{psychologistStrings.contact.title}</ContactTitle>
            <ContactSubtitle>{psychologistStrings.contact.subtitle}</ContactSubtitle>
          </ContactHeader>
          
          <ContactForm>
            <FormGrid>
              <FormGroup>
                <Label htmlFor="name">{psychologistStrings.contact.form.nameLabel}</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={psychologistStrings.contact.form.namePlaceholder}
                  disabled={isLoading}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">{psychologistStrings.contact.form.emailLabel}</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={psychologistStrings.contact.form.emailPlaceholder}
                  disabled={isLoading}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="phone">{psychologistStrings.contact.form.phoneLabel}</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={psychologistStrings.contact.form.phonePlaceholder}
                  disabled={isLoading}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">{psychologistStrings.contact.form.messageLabel}</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder={psychologistStrings.contact.form.messagePlaceholder}
                  disabled={isLoading}
                />
              </FormGroup>
              
              <SubmitButton onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? (
                  <>
                    {psychologistStrings.contact.form.sendingButton}
                    <LoadingSpinner />
                  </>
                ) : (
                  psychologistStrings.contact.form.submitButton
                )}
              </SubmitButton>
            </FormGrid>
          </ContactForm>
          
          <ContactInfo>
            <ContactInfoTitle>פרטי התקשרות</ContactInfoTitle>
            <ContactInfoItem>📞 {psychologistStrings.contact.info.phone}</ContactInfoItem>
            <ContactInfoItem>✉️ {psychologistStrings.contact.info.email}</ContactInfoItem>
            <ContactInfoItem>🕒 {psychologistStrings.contact.info.hours}</ContactInfoItem>
            
            <EmergencyInfo>
              <strong>⚠️ חירום נפשי:</strong><br />
              {psychologistStrings.contact.info.emergency}
            </EmergencyInfo>
          </ContactInfo>
        </ContactContainer>
      </ContactSection>

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterText>
            © 2025 {psychologistStrings.global.instituteName} - {psychologistStrings.global.subtitle}
          </FooterText>
          <FooterText>
            {psychologistStrings.global.therapistName} | {psychologistStrings.global.license}
          </FooterText>
          <FooterText style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
            מידע המוצג באתר זה אינו מהווה תחליף לייעוץ רפואי או פסיכולוגי מקצועי
          </FooterText>
        </FooterContent>
      </Footer>
    </div>
  );
};

export default PsychologistLandingPage;